import { effectScope, onScopeDispose, watchEffect } from 'vue';

import {
  convertToUnit,
  getScrollParents,
  hasScrollbar,
  IN_BROWSER,
  preventedScrollClass,
  propsFactory
} from '@components/utils';

import { requestNewFrame } from '../utils';

import type { EffectScope, PropType, Ref } from 'vue';

export interface IScrollStrategyData {
  root: Ref<HTMLElement | undefined>;
  contentEl: Ref<HTMLElement | undefined>;
  targetEl: Ref<HTMLElement | undefined>;
  isActive: Ref<boolean>;
  updateLocation: Ref<((event: Event) => void) | undefined>;
}

type TScrollStrategyFn = (data: IScrollStrategyData, props: IScrollStrategyProps, scope: EffectScope) => void;

const scrollStrategies = {
  none: null,
  close: closeScrollStrategy,
  block: blockScrollStrategy,
  reposition: repositionScrollStrategy
};

export interface IScrollStrategyProps {
  scrollStrategy: keyof typeof scrollStrategies | TScrollStrategyFn;
  contained: boolean | undefined;
}

export const makeScrollStrategyProps = propsFactory(
  {
    scrollStrategy: {
      type: [String, Function] as PropType<keyof typeof scrollStrategies | TScrollStrategyFn>,
      default: 'block',
      validator: (value: any) => typeof value === 'function' || value in scrollStrategies
    }
  },
  'scroll-strategy'
);

export function useScrollStrategies(props: IScrollStrategyProps, data: IScrollStrategyData): void {
  if (!IN_BROWSER) return;

  let scope: EffectScope | undefined;
  watchEffect(async () => {
    scope?.stop();

    if (!(data.isActive.value && props.scrollStrategy)) return;

    scope = effectScope();

    await new Promise((resolve) => setTimeout(resolve));

    scope.active &&
      scope.run(() => {
        if (typeof props.scrollStrategy === 'function') {
          props.scrollStrategy(data, props, scope!);
        } else {
          scrollStrategies[props.scrollStrategy]?.(data, props, scope!);
        }
      });
  });

  onScopeDispose(() => {
    scope?.stop();
  });
}

function closeScrollStrategy(data: IScrollStrategyData) {
  function onScroll() {
    data.isActive.value = false;
  }

  _bindScroll(data.targetEl.value ?? data.contentEl.value, onScroll);
}

function blockScrollStrategy(data: IScrollStrategyData, props: IScrollStrategyProps) {
  const offsetParent = data.root.value?.offsetParent as HTMLElement;
  const scrollElements = [
    ...new Set([
      ...getScrollParents(data.targetEl.value, props.contained ? offsetParent : undefined),
      ...getScrollParents(data.contentEl.value, props.contained ? offsetParent : undefined)
    ])
  ].filter((el) => !el.classList.contains(preventedScrollClass));

  const scrollbarWidth = window.innerWidth - document.documentElement.offsetWidth;
  const scrollableParent = ((el) => hasScrollbar(el) && el)(offsetParent || document.documentElement);

  if (scrollableParent) {
    data.root.value!.classList.add(preventedScrollClass);
  }

  scrollElements.forEach((el) => {
    el.style.setProperty('--cds-body-scroll-x', convertToUnit(-el.scrollLeft));
    el.style.setProperty('--cds-body-scroll-y', convertToUnit(-el.scrollTop));

    if (el !== document.documentElement) {
      el.style.setProperty('--cds-scrollbar-offset', convertToUnit(scrollbarWidth));
    }

    el.classList.add(preventedScrollClass);
  });

  onScopeDispose(() => {
    scrollElements.forEach((el) => {
      const x = parseFloat(el.style.getPropertyValue('--cds-body-scroll-x'));
      const y = parseFloat(el.style.getPropertyValue('--cds-body-scroll-y'));

      const scrollBehavior = el.style.scrollBehavior;

      el.style.removeProperty('--cds-body-scroll-x');
      el.style.removeProperty('--cds-body-scroll-y');
      el.style.removeProperty('--cds-scrollbar-offset');
      el.classList.remove(preventedScrollClass);

      el.scrollLeft = -x;
      el.scrollTop = -y;

      el.style.scrollBehavior = scrollBehavior;
    });

    if (scrollableParent) {
      data.root.value!.classList.remove(preventedScrollClass);
    }
  });
}

function repositionScrollStrategy(data: IScrollStrategyData, props: IScrollStrategyProps, scope: EffectScope) {
  let slow = false;
  let raf = -1;
  let ric = -1;

  function update(event: Event) {
    requestNewFrame(() => {
      const start = performance.now();
      data.updateLocation.value?.(event);
      const time = performance.now() - start;
      slow = time / (1000 / 60) > 2;
    });
  }

  ric = (typeof requestIdleCallback === 'undefined' ? (cb: Function) => cb() : requestIdleCallback)(() => {
    scope.run(() => {
      _bindScroll(data.targetEl.value ?? data.contentEl.value, (event) => {
        if (slow) {
          // If the position calculation is slow,
          // defer updates until scrolling is finished.
          // Browsers usually fire one scroll event per frame so
          // we just wait until we've got two frames without an event
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            raf = requestAnimationFrame(() => {
              update(event);
            });
          });
        } else {
          update(event);
        }
      });
    });
  });

  onScopeDispose(() => {
    typeof cancelIdleCallback !== 'undefined' && cancelIdleCallback(ric);
    cancelAnimationFrame(raf);
  });
}

/** @private */
function _bindScroll(el: HTMLElement | undefined, onScroll: (event: Event) => void) {
  const scrollElements = [document, ...getScrollParents(el)];
  scrollElements.forEach((el) => {
    el.addEventListener('scroll', onScroll, { passive: true });
  });

  onScopeDispose(() => {
    scrollElements.forEach((el) => {
      el.removeEventListener('scroll', onScroll);
    });
  });
}
